import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { trackCta } from "@/lib/track";

// Logo transparente
const LOGO_URL = "/images/justfit-logo-280.webp?v=2026";

const navLinks = [
  { href: "/#clubes", label: "Clubes" },
  { href: "/#servicos", label: "Serviços" },
  { href: "/#personal", label: "Personal Training" },
  { href: "/#testemunhos", label: "Testemunhos" },
];

const phoneClubs = [
  { id: "maia", name: "Clube da Maia", phone: "+351932317197", location: "Parada, Maia" },
  { id: "barcelos", name: "Clube de Barcelos", phone: "+351932317195", location: "Centro de Barcelos" },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    if (location.pathname !== "/" && href.startsWith("/#")) {
      window.location.href = href;
      return;
    }
    const sectionId = href.replace("/#", "#");
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-[#1A1A1A] shadow-lg" 
            : "bg-[#1A1A1A]"
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo transparente com filtro para ajustar o amarelo */}
            <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
              <img 
                src={LOGO_URL} 
                alt="Justfit Fitness Center" 
                width="400"
                height="171"
                className="h-12 lg:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="nav-link text-sm font-medium uppercase tracking-wider"
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <button onClick={() => setShowPhoneModal(true)} className="flex items-center gap-2 text-[#FED601] text-sm font-medium hover:text-[#E0BC00] transition-colors" data-testid="nav-call-button">
                <Phone size={16} />
                <span>Ligar Agora</span>
              </button>
              <button
                onClick={() => { trackCta("navbar_inscrever"); scrollToSection("/#contacto"); }}
                className="bg-[#FED601] text-black px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#E0BC00] transition-colors"
                data-testid="nav-cta-button"
              >
                Inscrever
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-[#1A1A1A] mobile-menu">
              <div className="flex flex-col items-center justify-center h-full gap-6 pt-16">
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => scrollToSection(link.href)}
                    className="text-xl font-bold text-white uppercase tracking-wider hover:text-[#FED601]"
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </motion.button>
                ))}
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  onClick={() => { setIsMobileMenuOpen(false); setShowPhoneModal(true); }}
                  className="flex items-center gap-2 text-[#FED601] text-lg font-bold uppercase tracking-wider"
                  data-testid="mobile-call-button"
                >
                  <Phone size={20} /> Ligar Agora
                </motion.button>
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  onClick={() => { trackCta("navbar_inscrever_mobile"); scrollToSection("/#contacto"); }}
                  className="btn-primary px-8 py-4 text-lg mt-4"
                  data-testid="mobile-cta-button"
                >
                  Inscrever Agora
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phone Club Selection Modal */}
      <AnimatePresence>
        {showPhoneModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowPhoneModal(false)} className="fixed inset-0 bg-black/50 z-[60]" />
            <motion.div initial={{ opacity: 0, scale: 0.9, y: -20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: -20 }} transition={{ type: "spring", damping: 25 }} className="fixed top-24 right-8 z-[60] w-80 bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="phone-modal">
              <div className="bg-[#1A1A1A] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Phone className="text-[#FED601]" size={22} />
                  <div>
                    <h3 className="text-white font-bold">Ligar Agora</h3>
                    <p className="text-gray-400 text-sm">Escolhe o clube</p>
                  </div>
                </div>
                <button onClick={() => setShowPhoneModal(false)} className="text-gray-400 hover:text-white"><X size={22} /></button>
              </div>
              <div className="p-4 space-y-3">
                {phoneClubs.map((club) => (
                  <a key={club.id} href={`tel:${club.phone}`} onClick={() => { trackCta("ligar_agora", club.id); setShowPhoneModal(false); }} className="w-full flex items-center gap-4 p-4 bg-gray-50 hover:bg-[#FED601]/10 rounded-xl transition-colors border border-gray-100 hover:border-[#FED601]" data-testid={`phone-club-${club.id}`}>
                    <div className="w-10 h-10 bg-[#FED601] rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-black" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-gray-900">{club.name}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12} />{club.location}</p>
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
